import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const BottomNavContext = createContext({ height: 0 });

export function BottomNavProvider({ children }) {
  const [navHeight, setNavHeight] = useState(70); // Start with a reasonable default height
  const navRef = useRef(null);
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  // Detect smaller screens
  const isSmallScreen = window.innerWidth <= 415;

  // Function to update height measurement
  const updateHeight = () => {
    if (navRef.current) {
      const height = navRef.current.clientHeight || 0;
      
      // Get the iOS safe area bottom inset (for home indicator and toolbar)
      let safeAreaBottom = 0;
      if (isIOS) {
        // Try to get the computed value of the CSS variable
        const safeAreaValue = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom').trim();
        if (safeAreaValue && safeAreaValue !== '0px') {
          safeAreaBottom = parseInt(safeAreaValue, 10) || 34; // Default to 34px if parsing fails
        } else {
          // Fallback values based on iPhone models
          safeAreaBottom = 34; // Default for iPhone X and newer
        }
      }
      
      // Calculate safety margin based on device and safe area
      const safetyMargin = isIOS 
        ? (safeAreaBottom + (isSmallScreen ? 20 : 30)) // Reduced from 70/90 to 20/30
        : 20; // Reduced from 50 to 20
        
      console.log('Nav height calculation:', { 
        navHeight: height, 
        safeAreaBottom, 
        safetyMargin, 
        total: height + safetyMargin 
      });
      
      setNavHeight(height + safetyMargin);
    }
  };

  // This effect runs once on mount to set viewport
  useEffect(() => {
    // Add viewport-fit=cover meta tag for iOS devices
    if (isIOS) {
      let metaViewport = document.querySelector('meta[name=viewport]');
      if (!metaViewport) {
        metaViewport = document.createElement('meta');
        metaViewport.name = 'viewport';
        document.head.appendChild(metaViewport);
      }
      metaViewport.content = 'width=device-width, initial-scale=1, viewport-fit=cover';
      
      // Add CSS variable for safe area to :root
      document.documentElement.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom, 34px)');
    }
  }, [isIOS]);

  // This effect handles height measurements
  useEffect(() => {
    // Schedule multiple measurements to ensure we catch the correct height
    // Initial measurement
    updateHeight();
    
    // Measure after a short delay (after initial render)
    const initialTimer = setTimeout(updateHeight, 50);
    
    // Also measure after content has fully loaded
    const loadTimer = setTimeout(updateHeight, 300);
    
    // Measure on resize and orientation change
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    
    // Force recalculation on route changes
    const handleRouteChange = () => {
      setTimeout(updateHeight, 100);
    };
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(loadTimer);
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [isIOS, isSmallScreen]);

  return (
    <BottomNavContext.Provider value={{ height: navHeight, navRef, isIOS, isSmallScreen, updateHeight }}>
      {children}
    </BottomNavContext.Provider>
  );
}

export function useBottomNav() {
  return useContext(BottomNavContext);
} 